import { useTranslation } from "react-i18next";
import { server } from "src/coreConfig";
import { setUserState, updateUserData } from "src/slices/users";
import { useDispatch, useSelector } from "src/store";
import axiosInstance from "src/utils/axios";
import useAsyncRequest from "./useAsyncRequest";
import { useCanDo } from "./useCando";

const userUrls = {
  get: `${server}/api/v2/user/user/get/`,
  address: {
    new: `${server}/api/v2/user/address/new/`,
    update: `${server}/api/v2/user/address/update/`,
    delete: `${server}/api/v2/user/address/delete/`,
    oldApi: `${server}/api/v2/user/address/`,
    uploadAddressPhoto: `${server}/api/v2/user/address/uploadAddressPhoto/`,
    updateTaxesLicenseStatus: `${server}/api/v2/user/address/updateTaxesLicenseStatus/`,
    requestGeoCorrection: `${server}/api/v2/user/address/requestGeoCorrection/`,
  },

  assisted: `${server}/api/v2/user/user/assisted/`,
  change_email: `${server}/api/v2/user/user/changeEmail/`,
  change_email_request: `${server}/api/v2/user/user/changeEmail/request/`,
  change_email_confirm: `${server}/api/v2/user/user/changeEmail/confirm/`,
  change_email_admin_request: `${server}/api/v2/user/user/changeEmail/adminRequest/`,
  change_email_requests_list: `${server}/api/v2/user/user/changeEmail/requests/list/`,
  change_email_requests_resolve: `${server}/api/v2/user/user/changeEmail/requests/resolve/`,
  change_email_requests_report: `${server}/api/v2/user/user/changeEmail/requests/report/`,
  company: `${server}/api/v2/user/company/`,
  company_update: `${server}/api/v2/user/company/update/`,
  company_member: `${server}/api/v2/user/company/member/`,
  companySearch: `${server}/api/v2/user/company/search/`,
  document: {
    download: `${server}/api/v2/user/document/download/`,
    list: `${server}/api/v2/user/document/`,
  },
  // document: `${server}/api/v2/user/document/`,
  folder: `${server}/api/v2/user/folder/`,
  get_device_uuid: `${server}/api/v2/user/device/getDevUID/`,
  identity: {
    base: `${server}/api/v2/user/identity/`,
    addLegalFile: `${server}/api/v2/user/identity/addLegalFile/`,
    available: `${server}/api/v2/user/identity/available/`,
    share: `${server}/api/v2/user/identity/share/`,
    unshare: `${server}/api/v2/user/identity/unshare/`,
    sharedMembers: `${server}/api/v2/user/identity/sharedMembers/`,
    session: `${server}/api/v2/user/identity/session/`,
    uploadAvatar: `${server}/api/v2/user/identity/uploadAvatar/`,
    checkIdentity: `${server}/api/v2/user/identity/checkIdentity/`,
    resolve: `${server}/api/v2/user/identity/resolve/`,
    delete: `${server}/api/v2/user/identity/delete/`,
    new: `${server}/api/v2/user/identity/new/`,
    setSession: `${server}/api/v2/user/identity/setSessionIdentity/`,
    setMain: `${server}/api/v2/user/identity/setMain/`,
    update: `${server}/api/v2/user/identity/update/`,
    get: `${server}/api/v2/user/identity/get/`,
  },
  identityCompanies: `${server}/api/v2/user/company/identityCompanies/`,
  identityFile: `${server}/api/v2/user/identity/addFile/`,
  logout: `${server}/api/v2/user/user/logout/`,
  sessions: {
    list: `${server}/api/v2/user/user/sessions/`,
    revokeAll: `${server}/api/v2/user/user/sessions/revokeAll/`,
  },
  phone: {
    delete: `${server}/api/v2/user/phone/delete/`,
    new: `${server}/api/v2/user/phone/new/`,
    update: `${server}/api/v2/user/phone/update/`,
    verify: `${server}/api/v2/user/phone/verify/`,
    adminVerify: `${server}/api/v2/user/phone/adminVerify/`,
  },
  pinAuthorization: `${server}/api/v2/user/user/pinAuthorization/`,
  role: {
    create: `${server}/api/v2/user/role/create/`,
    addPermissions: `${server}/api/v2/user/role/addPermissions/`,
    deletePermission: `${server}/api/v2/user/role/deletePermission/`,
    detail: `${server}/api/v2/user/role/detail/`,
    permissionCoverage: `${server}/api/v2/user/role/permissionCoverage/`,
    list: `${server}/api/v2/user/role/`,
    permissionRequestsList: `${server}/api/v2/user/role/permissionRequests/list/`,
    permissionRequestsResolve: `${server}/api/v2/user/role/permissionRequests/resolve/`,
    options: `${server}/api/v2/user/role/options/`,
    addUser: `${server}/api/v2/user/role/addUser/`,
    removeUser: `${server}/api/v2/user/role/removeUser/`,
    getUserPermissions: `${server}/api/v2/user/role/getUserPermissions/`,
    getUserLogs: `${server}/api/v2/user/role/getUserLogs/`,
    addMember: `${server}/api/v2/user/role/addMember/`,
    deleteMember: `${server}/api/v2/user/role/deleteMember/`,
    updateMember: `${server}/api/v2/user/role/updateMember/`,
  },
  search: `${server}/api/v2/user/user/search/`,
  setPin: `${server}/api/v2/user/user/setPin/`,
  update: `${server}/api/v2/user/user/update/`,
  userAddresses: `${server}/api/v2/user/address/userAddresses/`,
  userIdentities: `${server}/api/v2/user/identity/userIdentities/`,
  getIpInfo: `${server}/api/v2/user/user/getIpInfo/`,
};
const useUser = () => {
  const { currentCustomer } = useSelector((state) => state.customers);
  const { user, ipLocations, members, roles } = useSelector(
    (state) => state.users,
  );
  const { adminMode } = useSelector((state) => state.settings);
  const { api } = useAsyncRequest();
  const { canDo } = useCanDo("nets_user");
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const checkIdentity = async (data) => {
    try {
      var res = await api(
        userUrls.identity.checkIdentity,
        {
          ...data,
          customer_id: currentCustomer?.id,
          mode: adminMode ? "admin" : "user",
        },
        {},
        t("Verificando identidad"),
        true,
      );
      if (res.data?.res === 1) {
        return res.data.data;
      } else {
        throw new Error(res.data.message);
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  };
  const resolveIdentity = async ({ documentTypeId, nationalId }) => {
    const res = await api(
      userUrls.identity.resolve,
      {
        id: documentTypeId,
        national_id: nationalId,
        customer_id: currentCustomer?.id,
        mode: adminMode ? "admin" : "user",
      },
      {},
      t("Consultando documento"),
      true,
    );
    if (res.data?.res === 1) {
      return res.data.data;
    }
    throw new Error(
      res.data?.message || t("No fue posible consultar el documento"),
    );
  };

  const getUser = async (id, onBackground) => {
    var res = await api(
      userUrls.get,
      {
        id: id,
        customer_id: currentCustomer.id,
        mode: adminMode ? "admin" : "user",
      },
      {},
      t("Obteniendo usuario"),
      onBackground,
    );
    return res.data.data;
  };

  const addUserRole = async ({ roleId, userId }) => {
    return await api(
      userUrls.role.addUser,
      {
        role_id: roleId,
        user_id: userId,
        customer_id: currentCustomer.id,
      },
      {},
      t("Agregando rol"),
    );
  };
  const removeUserRole = async ({ roleId, userId }) => {
    return await api(
      userUrls.role.removeUser,
      {
        role_id: roleId,
        user_id: userId,
        customer_id: currentCustomer.id,
      },
      {},
      t("Eliminando rol"),
    );
  };
  const roleList = async () => {
    var res = await api(
      userUrls.role.list,
      {
        customer_id: currentCustomer.id,
        mode: adminMode ? "admin" : "user",
      },
      {},
      t("Obteniendo roles"),
    );
    console.log("roleList", res);
    dispatch(
      setUserState({
        roles: res.data.data.roles,
        members: res.data.data.members,
        role_users: res.data.data.role_users,
      }),
    );
  };

  const createRole = async ({ name }) => {
    const res = await api(
      userUrls.role.create,
      {
        name,
        customer_id: currentCustomer.id,
      },
      {},
      t("Creando rol"),
    );
    await roleList();
    return res.data;
  };

  const addMember = async (data) => {
    var res = await api(
      userUrls.role.addMember,
      {
        ...data,
        customer_id: currentCustomer.id,
      },
      {},
      t("Agregando miembro"),
    );
    // add or update member

    var membersUpdated = members.filter((item) => {
      return item.id !== res.data.data.id;
    });
    membersUpdated = [res.data.data, ...membersUpdated];

    dispatch(
      setUserState({
        members: membersUpdated,
      }),
    );
  };

  const deleteMember = async (id) => {
    var res = await api(
      userUrls.role.deleteMember,
      {
        user_id: id,
        customer_id: currentCustomer.id,
      },
      {},
      t("Eliminando miembro"),
    );
    dispatch(
      setUserState({
        members: members.filter((item) => item.user.id !== id),
      }),
    );
  };

  const getUserPermissions = async (userId) => {
    var res = await api(
      userUrls.role.getUserPermissions,
      {
        user_id: userId,
        customer_id: currentCustomer.id,
      },
      {},
      t("Obteniendo permisos"),
    );
    return res.data.data;
  };
  const getUserLogs = async (userId, page) => {
    var res = await api(
      userUrls.role.getUserLogs,
      {
        user_id: userId,
        customer_id: currentCustomer.id,
        page: page || 1,
        page_size: 20,
      },
      {},
      null,
    );
    return res.data.data;
  };

  const getPermissionRequests = async ({ status, page, pageSize } = {}) => {
    const res = await api(
      userUrls.role.permissionRequestsList,
      {
        customer_id: currentCustomer.id,
        status: status || "pending,in_review",
        page: page || 1,
        page_size: pageSize || 30,
      },
      {},
      null,
    );
    return res.data.data;
  };

  const getPermissionCoverage = async ({
    permissionId,
    roleId,
    search,
  } = {}) => {
    const res = await api(
      userUrls.role.permissionCoverage,
      {
        customer_id: currentCustomer.id,
        permission_id: permissionId,
        role_id: roleId || null,
        search: search || "",
      },
      {},
      t("Evaluando cobertura de permiso"),
    );
    return res.data.data;
  };

  const resolvePermissionRequest = async ({
    requestId,
    decision,
    strategy,
    roleId,
    newRoleName,
    decisionNotes,
  }) => {
    const res = await api(
      userUrls.role.permissionRequestsResolve,
      {
        customer_id: currentCustomer.id,
        request_id: requestId,
        decision,
        strategy: strategy || "existing_role",
        role_id: roleId || null,
        new_role_name: newRoleName || "",
        decision_notes: decisionNotes || "",
      },
      {},
      decision === "approve"
        ? t("Aplicando solicitud")
        : t("Rechazando solicitud"),
    );
    return res.data;
  };
  const getIpInfo = async (ip) => {
    if (ipLocations[ip]) return ipLocations[ip];
    var res = await api(
      userUrls.getIpInfo,
      {
        ip: ip,
      },
      {},
      null,
    );
    dispatch(
      setUserState({
        ipLocations: {
          ...ipLocations,
          [ip]: res.data.data,
        },
      }),
    );
    return res.data.data;
  };

  const getActiveSessions = async () => {
    const res = await api(userUrls.sessions.list, {}, {}, null, true);
    if (res?.data?.res !== 1) {
      throw new Error(
        res?.data?.message || t("No se pudieron cargar las sesiones"),
      );
    }
    return res.data.data;
  };

  const revokeAllSessions = async () => {
    const res = await api(
      userUrls.sessions.revokeAll,
      {},
      {},
      t("Cerrando sesiones"),
    );
    if (res?.data?.res !== 1) {
      throw new Error(
        res?.data?.message || t("No se pudieron cerrar las sesiones"),
      );
    }
    return res.data.data;
  };

  const searchUser = async (data) => {
    console.log(data);
    try {
      var res = await api(
        userUrls.search,
        {
          customer_id: currentCustomer.id,
          ...data,
          mode: adminMode ? "admin" : "user",
        },
        {},
        t("Buscando usuario"),
      );
      if (res && res.data?.data) {
        return res.data.data;
      } else {
        return [];
      }
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const addAddress = async (data) => {
    try {
      var res = await api(
        userUrls.address.new,
        {
          ...data,
          customer_id: currentCustomer.id,
        },
        {},
        t("Agregando direcci�n"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };
  const updateAddress = async (data) => {
    try {
      var res = await api(
        userUrls.address.update,
        {
          ...data,
          customer_id: currentCustomer.id,
        },
        {},
        t("Actualizando direcci�n"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };
  const deleteAddress = async (data) => {
    try {
      var res = await api(
        userUrls.address.delete,
        {
          ...data,
          customer_id: currentCustomer.id,
        },
        {},
        t("Eliminando direcci�n"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };

  const updateTaxesLicenseStatus = async (data) => {
    try {
      var res = await api(
        userUrls.address.updateTaxesLicenseStatus,
        {
          ...data,
          customer_id: currentCustomer.id,
        },
        {},
        t("Actualizando estado de licencia de impuestos"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };

  // uploadAddressPhoto
  const uploadAddressPhoto = async (data) => {
    try {
      var f = new FormData();
      f.append("photo", data.photo);
      Object.keys(data).forEach((key) => {
        if (key !== "photo") {
          // if value is object
          if (typeof data[key] === "object") {
            f.append(key, JSON.stringify(data[key]));
          } else {
            f.append(key, data[key]);
          }
        }
      });
      var res = await api(
        userUrls.address.uploadAddressPhoto,
        f,
        {},
        t("Subiendo foto de direcci�n"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };

  // get identity
  const getIdentity = async (id) => {
    try {
      var res = await api(
        userUrls.identity.get,
        {
          id: id,
          customer_id: currentCustomer.id,
        },
        {},
        t("Obteniendo identidad"),
      );
      if (res.status !== 200) {
        throw new Error(res.data.message || "Error al obtener identidad");
      }

      return res.data.data;
    } catch (error) {
      console.error("Error getting identity:", error);
      throw error;
    }
  };

  const getAvailableIdentities = async () => {
    const payload = {
      mode: adminMode ? "admin" : "user",
    };
    if (currentCustomer?.id) {
      payload.customer_id = currentCustomer.id;
    }

    const res = await api(
      userUrls.identity.available,
      payload,
      {},
      t("Obteniendo identidades"),
      true,
    );

    if (res.data?.res !== 1) {
      throw new Error(
        res.data?.message || t("No se pudieron cargar identidades"),
      );
    }

    return res.data.data || [];
  };

  const getSessionIdentity = async () => {
    const payload = {
      mode: adminMode ? "admin" : "user",
    };
    if (currentCustomer?.id) {
      payload.customer_id = currentCustomer.id;
    }

    const res = await api(
      userUrls.identity.session,
      payload,
      {},
      t("Recuperando identidad activa"),
      true,
    );

    const status = res?.response?.status || res?.status;
    if (status === 404) {
      const error = new Error(
        t("El endpoint de sesi�n de identidad no est� disponible"),
      );
      error.status = 404;
      error.code = "identity_session_endpoint_missing";
      throw error;
    }

    if (res.data?.res !== 1) {
      const error = new Error(
        res.data?.message || t("No se pudo recuperar la identidad activa"),
      );
      error.status = status;
      throw error;
    }

    return res.data.data || null;
  };

  const selectSessionIdentity = async (identityId) => {
    const payload = {
      id: identityId,
      mode: adminMode ? "admin" : "user",
    };
    if (currentCustomer?.id) {
      payload.customer_id = currentCustomer.id;
    }

    const res = await api(
      userUrls.identity.setSession,
      payload,
      {},
      t("Seleccionando identidad"),
    );

    if (res.data?.res !== 1) {
      throw new Error(
        res.data?.message || t("No se pudo seleccionar la identidad"),
      );
    }

    return res.data.active_identity || null;
  };

  const shareIdentity = async ({ identityId, memberEmail, role }) => {
    const payload = {
      identity_id: identityId,
      member_email: memberEmail,
      role: role || "view",
      mode: adminMode ? "admin" : "user",
    };
    if (currentCustomer?.id) {
      payload.customer_id = currentCustomer.id;
    }

    const res = await api(
      userUrls.identity.share,
      payload,
      {},
      t("Compartiendo identidad"),
    );

    if (res.data?.res !== 1) {
      throw new Error(
        res.data?.message || t("No se pudo compartir la identidad"),
      );
    }

    return res.data.data;
  };

  const getIdentitySharedMembers = async (identityId) => {
    const payload = {
      identity_id: identityId,
      mode: adminMode ? "admin" : "user",
    };
    if (currentCustomer?.id) {
      payload.customer_id = currentCustomer.id;
    }

    const res = await api(
      userUrls.identity.sharedMembers,
      payload,
      {},
      t("Obteniendo usuarios compartidos"),
      true,
    );

    if (res.data?.res !== 1) {
      throw new Error(
        res.data?.message ||
          t("No se pudo obtener la compartici�n de identidad"),
      );
    }

    return res.data.data || [];
  };

  const unshareIdentity = async ({ identityId, memberId }) => {
    const payload = {
      identity_id: identityId,
      member_id: memberId,
      mode: adminMode ? "admin" : "user",
    };
    if (currentCustomer?.id) {
      payload.customer_id = currentCustomer.id;
    }

    const res = await api(
      userUrls.identity.unshare,
      payload,
      {},
      t("Revocando compartici�n"),
    );

    if (res.data?.res !== 1) {
      throw new Error(
        res.data?.message ||
          t("No se pudo revocar la compartici�n de identidad"),
      );
    }

    return res.data.data;
  };

  // addLegalFile
  const addIdentityLegalFile = async (data) => {
    try {
      var f = new FormData();
      f.append("file", data.file);
      Object.keys(data).forEach((key) => {
        if (key !== "file") {
          // if value is object
          if (typeof data[key] === "object") {
            f.append(key, JSON.stringify(data[key]));
          } else {
            f.append(key, data[key]);
          }
        }
      });
      var res = await api(
        userUrls.identity.addLegalFile,
        f,
        {},
        t("Agregando archivo legal"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };
  // updload identity avatar
  const uploadIdentityAvatar = async (data) => {
    try {
      var f = new FormData();
      Object.keys(data).forEach((key) => {
        if (key !== "avatar") {
          f.append(key, data[key]);
        }
      });
      f.append("avatar", data.avatar);
      var res = await api(
        userUrls.identity.uploadAvatar,
        f,
        {},
        t("Subiendo avatar"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };

  // add phone
  const addPhone = async (data) => {
    try {
      var res = await api(
        userUrls.phone.new,
        {
          ...data,
          customer_id: currentCustomer.id,
        },
        {},
        t("Agregando tel�fono"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };

  // updatePhone
  const updatePhone = async (data) => {
    try {
      var res = await api(
        userUrls.phone.update,
        {
          ...data,
          customer_id: currentCustomer.id,
        },
        {},
        t("Actualizando tel�fono"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };

  // verify phone
  const verifyPhone = async (data) => {
    try {
      var res = await api(
        userUrls.phone.verify,
        {
          ...data,
          customer_id: currentCustomer.id,
        },
        {},
        t("Verificando tel�fono"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };
  const adminVerifyPhone = async (data) => {
    try {
      var res = await api(
        userUrls.phone.adminVerify,
        {
          ...data,
          customer_id: currentCustomer.id,
        },
        {},
        t("Verificando tel�fono"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };
  // delete phone
  const deletePhone = async (id) => {
    try {
      var res = await api(
        userUrls.phone.delete,
        {
          id: id,
          customer_id: currentCustomer.id,
        },
        {},
        t("Eliminando tel�fono"),
      );

      return res;
    } catch (e) {
      console.error(e);
    }
  };

  const documentDownload = async ({ documentId, customerId, mode } = {}) => {
    if (!documentId) {
      throw new Error(t("Documento inv�lido"));
    }

    const payload = {
      id: documentId,
      mode: mode || (adminMode ? "admin" : "user"),
    };

    const resolvedCustomerId = customerId || currentCustomer?.id;
    if (resolvedCustomerId) {
      payload.customer_id = resolvedCustomerId;
    }

    return await axiosInstance.post(userUrls.document.download, payload, {
      responseType: "blob",
    });
  };

  const requestEmailChange = async ({ newEmail }) => {
    const res = await api(
      userUrls.change_email_request,
      {
        new_email: newEmail,
      },
      {},
      t("Solicitando cambio de correo"),
    );

    if (res.data?.res !== 1) {
      throw new Error(
        res.data?.message || t("No se pudo solicitar el cambio de correo"),
      );
    }

    return res.data;
  };

  const confirmEmailChange = async ({
    requestId,
    currentEmailCode,
    newEmailCode,
  }) => {
    const res = await api(
      userUrls.change_email_confirm,
      {
        request_id: requestId,
        current_email_code: currentEmailCode,
        new_email_code: newEmailCode,
      },
      {},
      t("Confirmando cambio de correo"),
    );

    if (res.data?.res !== 1) {
      throw new Error(
        res.data?.message || t("No se pudo confirmar el cambio de correo"),
      );
    }

    if (res.data?.data?.id === user?.id) {
      dispatch(updateUserData(res.data.data));
    }

    return res.data;
  };

  const adminRequestEmailChange = async ({ userId, newEmail }) => {
    const res = await api(
      userUrls.change_email_admin_request,
      {
        user_id: userId,
        new_email: newEmail,
        customer_id: currentCustomer.id,
      },
      {},
      t("Creando solicitud de cambio de correo"),
    );

    if (res.data?.res !== 1) {
      throw new Error(
        res.data?.message || t("No se pudo crear la solicitud de cambio"),
      );
    }

    return res.data;
  };

  const listEmailChangeRequests = async ({
    status,
    searchText,
    page,
    pageSize,
  } = {}) => {
    const res = await api(
      userUrls.change_email_requests_list,
      {
        customer_id: currentCustomer.id,
        status: status || "all",
        search_text: searchText || "",
        page: page || 1,
        page_size: pageSize || 30,
      },
      {},
      t("Obteniendo solicitudes de correo"),
      true,
    );

    if (res.data?.res !== 1) {
      throw new Error(
        res.data?.message || t("No se pudieron cargar las solicitudes"),
      );
    }

    return res.data.data;
  };

  const resolveEmailChangeRequest = async ({
    requestId,
    decision,
    denialReason,
  }) => {
    const res = await api(
      userUrls.change_email_requests_resolve,
      {
        customer_id: currentCustomer.id,
        request_id: requestId,
        decision,
        denial_reason: denialReason || "",
      },
      {},
      decision === "approve"
        ? t("Aprobando solicitud de correo")
        : t("Rechazando solicitud de correo"),
    );

    if (res.data?.res !== 1) {
      throw new Error(
        res.data?.message || t("No se pudo procesar la solicitud"),
      );
    }

    return res.data;
  };

  const downloadEmailChangeReport = async (
    { status, searchText } = {},
    format = "xlsx",
  ) => {
    return await axiosInstance.post(
      userUrls.change_email_requests_report,
      {
        customer_id: currentCustomer.id,
        status: status || "all",
        search_text: searchText || "",
        download_xlsx: format === "xlsx",
        download_pdf: format === "pdf",
        mode: adminMode ? "admin" : "user",
      },
      {
        responseType: "blob",
      },
    );
  };

  const requestAddressGeoCorrection = async ({ addressId, message, url }) => {
    const res = await api(
      userUrls.address.requestGeoCorrection,
      {
        id: addressId,
        message: message || "",
        url: url || null,
        customer_id: currentCustomer?.id,
        mode: adminMode ? "admin" : "user",
      },
      {},
      t("Solicitando correcci�n de direcci�n"),
    );

    return res?.data || res;
  };

  return {
    role: {
      createRole,
      addUser: addUserRole,
      removeUser: removeUserRole,
      list: roleList,
      getUserPermissions: getUserPermissions,
      getUserLogs: getUserLogs,
      getPermissionCoverage,
      getPermissionRequests,
      resolvePermissionRequest,
      addMember,
      deleteMember,
      updateMember: addMember,
    },
    userAddress: {
      new: addAddress,
      update: updateAddress,
      uploadPhoto: uploadAddressPhoto,
      updateTaxesLicenseStatus,
      delete: deleteAddress,
      requestGeoCorrection: requestAddressGeoCorrection,
    },
    identity: {
      checkIdentity,
      resolveIdentity,
      getIdentity,
      getAvailableIdentities,
      getIdentitySharedMembers,
      getSessionIdentity,
      shareIdentity,
      selectSessionIdentity,
      unshareIdentity,
      addIdentityLegalFile,
      uploadIdentityAvatar,
    },
    searchUser,
    getUser,
    getIpInfo,
    sessions: {
      list: getActiveSessions,
      revokeAll: revokeAllSessions,
    },
    document: {
      download: documentDownload,
    },
    emailChange: {
      request: requestEmailChange,
      confirm: confirmEmailChange,
      adminRequest: adminRequestEmailChange,
      listRequests: listEmailChangeRequests,
      resolveRequest: resolveEmailChangeRequest,
      downloadReport: downloadEmailChangeReport,
    },
    userPhones: {
      add: addPhone,
      update: updatePhone,
      verify: verifyPhone,
      adminVerify: adminVerifyPhone,
      delete: deletePhone,
    },
  };
};

export default useUser;
